(() => {
  'use strict';

  const currentScript = document.currentScript;
  const assetBase = currentScript?.src || Array.from(document.scripts).find((script) => script.src.includes('oriavi-line-icons.js'))?.src || '';
  const ICON_ATTR = 'data-oriavi-line-icon';
  const NORMALIZED_ATTR = 'data-oriavi-icon-normalized';
  const STYLE_LINK_ID = 'oriavi-icon-system-css';
  const SKIP_SELECTOR = 'script, style, textarea, input, select, option, svg, math, [contenteditable="true"], [data-oriavi-icon-skip], #gtx-trans, #gtx-host, [class^="gtx-"], [class*=" gtx-"]';
  const INTERACTIVE_SELECTOR = 'a, button, summary, [role="button"], .button, [class*="button"], [class*="link"], [data-oriavi-icon-text]';
  const TOKENS = ['↗️', '➡️', '↖️', '↗', '➡', '↖', '→', '←', '↑', '↓'];

  const utilityHints = [
    'search', 'cart', 'bag', 'menu', 'close', 'times', 'chevron', 'arrow',
    'accordion', 'plus', 'minus', 'filter', 'sort', 'share', 'zoom', 'play',
    'pause', 'account', 'user', 'wishlist', 'heart', 'location', 'pin',
    'truck', 'delivery', 'return', 'guarantee', 'lock', 'secure', 'email',
    'phone', 'copy', 'external', 'expand', 'collapse', 'slider', 'next', 'prev'
  ];

  const excludedHints = [
    'logo', 'brand', 'payment', 'visa', 'mastercard', 'amex', 'paypal',
    'apple-pay', 'google-pay', 'social', 'instagram', 'facebook', 'pinterest',
    'tiktok', 'youtube', 'twitter', 'x-logo', 'flag', 'country', 'stamped'
  ];

  function installStylesheet() {
    if (document.getElementById(STYLE_LINK_ID) || !assetBase) return;
    const link = document.createElement('link');
    link.id = STYLE_LINK_ID;
    link.rel = 'stylesheet';
    link.href = new URL('oriavi-icon-system.css', assetBase).href;
    document.head.appendChild(link);
  }

  function createIcon(kind) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute(ICON_ATTR, kind);
    svg.classList.add('oriavi-icon', `oriavi-icon--${kind}`);

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const paths = {
      'arrow-right': 'M5 12h14M13 6l6 6-6 6',
      'arrow-left': 'M19 12H5M11 6l-6 6 6 6',
      'arrow-up': 'M12 19V5M6 11l6-6 6 6',
      'arrow-down': 'M12 5v14M18 13l-6 6-6-6',
      'arrow-up-right': 'M7 17 17 7M8 7h9v9',
      'arrow-up-left': 'M17 17 7 7M16 7H7v9'
    };

    path.setAttribute('d', paths[kind] || paths['arrow-right']);
    svg.appendChild(path);
    return svg;
  }

  function tokenKind(token) {
    if (token.startsWith('↗')) return 'arrow-up-right';
    if (token.startsWith('↖')) return 'arrow-up-left';
    if (token === '←') return 'arrow-left';
    if (token === '↑') return 'arrow-up';
    if (token === '↓') return 'arrow-down';
    return 'arrow-right';
  }

  function isEligibleTextNode(node) {
    const parent = node.parentElement;
    return Boolean(parent && !parent.closest(SKIP_SELECTOR) && parent.closest(INTERACTIVE_SELECTOR));
  }

  function replaceTextNode(node) {
    if (!node || node.nodeType !== Node.TEXT_NODE || !isEligibleTextNode(node)) return;
    const value = node.nodeValue;
    if (!value || !TOKENS.some((token) => value.includes(token))) return;

    const fragment = document.createDocumentFragment();
    let remaining = value;

    while (remaining.length) {
      let matchIndex = -1;
      let matchToken = '';

      for (const token of TOKENS) {
        const index = remaining.indexOf(token);
        if (index !== -1 && (matchIndex === -1 || index < matchIndex || (index === matchIndex && token.length > matchToken.length))) {
          matchIndex = index;
          matchToken = token;
        }
      }

      if (matchIndex === -1) {
        fragment.appendChild(document.createTextNode(remaining));
        break;
      }

      if (matchIndex > 0) fragment.appendChild(document.createTextNode(remaining.slice(0, matchIndex)));
      fragment.appendChild(createIcon(tokenKind(matchToken)));
      remaining = remaining.slice(matchIndex + matchToken.length);
    }

    node.replaceWith(fragment);
  }

  function descriptor(svg) {
    const owner = svg.closest('button, a, summary, label, [class], [aria-label], [title]');
    return [
      svg.getAttribute('class'),
      svg.getAttribute('aria-label'),
      svg.getAttribute('title'),
      owner?.getAttribute('class'),
      owner?.getAttribute('aria-label'),
      owner?.getAttribute('title')
    ].filter(Boolean).join(' ').toLowerCase();
  }

  function normalizeSvg(svg) {
    if (!(svg instanceof SVGElement) || svg.hasAttribute(NORMALIZED_ATTR) || svg.hasAttribute(ICON_ATTR)) return;

    const info = descriptor(svg);
    if (!info || excludedHints.some((hint) => info.includes(hint))) return;
    if (!utilityHints.some((hint) => info.includes(hint))) return;

    svg.setAttribute(NORMALIZED_ATTR, 'true');
    svg.setAttribute('focusable', 'false');
    if (!svg.hasAttribute('aria-label') && !svg.hasAttribute('role')) svg.setAttribute('aria-hidden', 'true');

    svg.querySelectorAll('path, line, polyline, circle, rect').forEach((node) => {
      if (node.getAttribute('stroke') && node.getAttribute('stroke') !== 'none') {
        if (!node.hasAttribute('stroke-linecap')) node.setAttribute('stroke-linecap', 'round');
        if (!node.hasAttribute('stroke-linejoin')) node.setAttribute('stroke-linejoin', 'round');
      }
    });

    if (info.includes('external') || info.includes('up-right')) svg.dataset.oriaviMotion = 'up-right';
    else if (info.includes('prev') || info.includes('left')) svg.dataset.oriaviMotion = 'left';
    else if (info.includes('next') || info.includes('arrow') || info.includes('chevron')) svg.dataset.oriaviMotion = 'right';
  }

  function processRoot(root) {
    if (!root) return;

    if (root.nodeType === Node.TEXT_NODE) {
      replaceTextNode(root);
      return;
    }

    if (![Node.ELEMENT_NODE, Node.DOCUMENT_FRAGMENT_NODE, Node.DOCUMENT_NODE].includes(root.nodeType)) return;
    if (root.nodeType === Node.ELEMENT_NODE && root.matches(SKIP_SELECTOR)) return;

    const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return isEligibleTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });

    const textNodes = [];
    while (textWalker.nextNode()) textNodes.push(textWalker.currentNode);
    textNodes.forEach(replaceTextNode);

    if (root.matches?.('svg')) normalizeSvg(root);
    root.querySelectorAll?.('svg').forEach(normalizeSvg);
  }

  function init() {
    installStylesheet();
    processRoot(document.body);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(processRoot);
        if (mutation.type === 'characterData') replaceTextNode(mutation.target);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
